import { BrowserRouter, Routes, Route } from 'react-router-dom'
import { RedirectToSignIn, SignedIn, SignedOut } from '@clerk/clerk-react'
import { lazy, Suspense } from 'react'
import ProjectPage from './pages/ProjectPage'
import OutlinePage from './pages/OutlinePage'
import AdminPage from './pages/AdminPage'
import SharePage from './pages/SharePage'
import ActivityTracker from './components/ActivityTracker'
import LockoutHost from './components/LockoutHost'
import FlightHoverCard from './components/FlightHoverCard'

// /sketch is lazy-loaded — only downloaded when the user opens it via /sketch command
const SketchPage = lazy(() => import('./pages/SketchPage'))
// /journey is the public, token-accessed client experience (no Clerk) — lazy-loaded
const JourneyPage = lazy(() => import('./journey/JourneyPage'))
const InspirationPage = lazy(() => import('./pages/InspirationPage'))
// /present is the dual-gated design-presentation surface (team Clerk / client token) — lazy-loaded
const PresentPage = lazy(() => import('./pages/PresentPage'))
// /contractor is the public, token-accessed GC portal (no Clerk) — lazy-loaded
const ContractorPortalPage = lazy(() => import('./pages/ContractorPortalPage'))
// /intake is the public, token-accessed lead-stage intake instrument (no Clerk) — lazy-loaded
const LeadIntakePage = lazy(() => import('./pages/LeadIntakePage'))
// /modeler is the 3D BIM authoring tool (Clerk, per-Concept) — lazy-loaded so yjs/three/
// WASM never enter the main bundle (docs/MODELER.md)
const ModelerPage = lazy(() => import('./bim/ModelerPage'))
// /field is the standalone offline-capable Field Observation surface (Construction Gates
// Phase C — docs/FIELD_OBSERVATIONS.md). Own service worker scope; lazy-loaded.
const FieldPage = lazy(() => import('./pages/FieldPage'))
// /qc is the QC Markup tool opened in its own tab (docs/QC_WORKFLOW.md) — lazy-loaded.
const QcPage = lazy(() => import('./pages/QcPage'))
// /docqc is AlderQC's document markup for the Documents list, generalized beyond drawing sheets
// (docs/DOCUMENT_MARKUP.md) — lazy-loaded, same standalone-tab convention as /qc.
const DocQcPage = lazy(() => import('./pages/DocQcPage'))
// /spec is AlderSPEC — the self-contained spec tool in its own tab (docs/ALDERSPEC.md) — lazy-loaded.
const SpecWorkspacePage = lazy(() => import('./pages/SpecWorkspacePage'))
// /punch is the public, token-accessed FOCUSED single-punch-item page (no Clerk, no PIN) reached
// via the compiled Punch List PDF's per-row QR code — docs/FIELD_OBSERVATIONS.md. Lazy-loaded.
const PunchItemPage = lazy(() => import('./pages/PunchItemPage'))
// /walk is the public, token-accessed AlderCAD walkthrough (no Clerk) — a view-only
// first-person walk of a model, shared with anyone via WalkShareModal (docs/MODELER.md).
// Lazy-loaded so yjs/three/WASM never enter the main bundle, same as /modeler. Lives in
// bim/ (not pages/) — the check-boundaries rule scopes the bim packages' imports there.
const WalkSharePage = lazy(() => import('./bim/WalkSharePage'))
// /background is the public, token-accessed consultant background page (no Clerk) —
// docs/CONSULTANT_BACKGROUNDS.md. Lazy-loaded.
const BackgroundPage = lazy(() => import('./pages/BackgroundPage'))
// /consultant-hub is one consultant team member's own Hub — personal token + PIN, no Clerk
// (docs/CONSULTANT_HUB.md). Lazy-loaded.
const ConsultantHubPage = lazy(() => import('./pages/ConsultantHubPage'))
// /cpa is the firm's accountant's reports center — personal token + PIN, no Clerk
// (docs/CPA_REPORTS.md). Lazy-loaded.
const CpaReportsPage = lazy(() => import('./pages/CpaReportsPage'))
// /pay is the public, token-accessed custom checkout page (no Clerk) — replaces sending clients
// to Stripe's own hosted invoice page (docs/STRIPE_PAYMENTS.md). Lazy-loaded so Stripe.js/Elements
// never enter the main bundle.
const PayPage = lazy(() => import('./pages/PayPage'))

// Suspense fallback for /docqc — without one, `fallback={null}` (this file's default for every
// other lazy route) leaves the freshly-opened tab genuinely blank while the DocQcPage chunk
// downloads, which read as "did the click even work?" (Matt, 2026-09-09). A plain inline
// component, not imported from DocQcPage.jsx itself — importing anything from that module here
// would pull it into the main bundle and defeat the lazy-load entirely. Styled to match
// DocQcPage's own loading/error screen so there's no visible handoff once the real page mounts.
function DocQcFallback() {
  return (
    <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', fontFamily: '"DM Sans",sans-serif', fontSize: 13, color: '#cfd8da', padding: 24, textAlign: 'center', background: '#1a2629' }}>
      Opening AlderQC…
    </div>
  )
}

export default function App() {
  return (
    <BrowserRouter>
      <Routes>
        <Route path="/project" element={<ProjectPage />} />
        <Route path="/admin" element={<AdminPage />} />
        <Route path="/share" element={<SharePage />} />
        <Route path="/journey" element={
          <Suspense fallback={null}>
            <JourneyPage />
          </Suspense>
        } />
        <Route path="/inspiration" element={
          <Suspense fallback={null}>
            <InspirationPage />
          </Suspense>
        } />
        <Route path="/present" element={
          <Suspense fallback={null}>
            <PresentPage />
          </Suspense>
        } />
        <Route path="/contractor" element={
          <Suspense fallback={null}>
            <ContractorPortalPage />
          </Suspense>
        } />
        <Route path="/sketch" element={
          <Suspense fallback={null}>
            <SketchPage />
          </Suspense>
        } />
        <Route path="/intake" element={
          <Suspense fallback={null}>
            <LeadIntakePage />
          </Suspense>
        } />
        <Route path="/modeler" element={
          <Suspense fallback={null}>
            <ModelerPage />
          </Suspense>
        } />
        <Route path="/field" element={
          <Suspense fallback={null}>
            <FieldPage />
          </Suspense>
        } />
        <Route path="/qc" element={
          <Suspense fallback={null}>
            <QcPage />
          </Suspense>
        } />
        <Route path="/docqc" element={
          <Suspense fallback={<DocQcFallback />}>
            <DocQcPage />
          </Suspense>
        } />
        <Route path="/spec" element={
          <Suspense fallback={null}>
            <SpecWorkspacePage />
          </Suspense>
        } />
        <Route path="/punch" element={
          <Suspense fallback={null}>
            <PunchItemPage />
          </Suspense>
        } />
        <Route path="/walk" element={
          <Suspense fallback={null}>
            <WalkSharePage />
          </Suspense>
        } />
        <Route path="/background" element={
          <Suspense fallback={null}>
            <BackgroundPage />
          </Suspense>
        } />
        <Route path="/consultant-hub" element={
          <Suspense fallback={null}>
            <ConsultantHubPage />
          </Suspense>
        } />
        <Route path="/cpa" element={
          <Suspense fallback={null}>
            <CpaReportsPage />
          </Suspense>
        } />
        <Route path="/pay" element={
          <Suspense fallback={null}>
            <PayPage />
          </Suspense>
        } />
        <Route path="/" element={<OutlinePage />} />
      </Routes>
      {/* Active-work log (docs/ACTIVITY_HUD_PLAN.md) — signed-in sessions only, renders nothing. */}
      <SignedIn><ActivityTracker /></SignedIn>
      {/* The daily lockouts on tools that open in their own window (AlderCAD, AlderQC, …). */}
      <SignedIn><LockoutHost /></SignedIn>
      {/* Hover any flight number anywhere for what it is (2026-10-17). */}
      <SignedIn><FlightHoverCard /></SignedIn>
    </BrowserRouter>
  )
}
